import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  DEBUG_SESSIONS_PAGE_SIZE,
  fetchConversationDebug,
  type ConversationDebugPayload,
} from '@/lib/conversationDebug.ts';

/**
 * Everything the backend knows about one conversation, read while the dialog is open.
 * The analysed sessions arrive a page at a time, by offset — the endpoint refuses a
 * window wider than fifty, so a long-running conversation has to be read through.
 */
export function useConversationDebug(conversationId: string, open: boolean) {
  const debug = useInfiniteQuery({
    queryKey: ['conversationDebug', conversationId],
    queryFn: ({ pageParam }) =>
      fetchConversationDebug(conversationId, {
        sessionsLimit: DEBUG_SESSIONS_PAGE_SIZE,
        sessionsOffset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const read = pages.reduce(
        (total, page) => total + page.chat_sessions.items.length,
        0,
      );
      return read < last.chat_sessions.total ? read : undefined;
    },
    enabled: open,
  });

  /*
   * The conversation itself as the first answer described it, with every
   * session read so far under it — the shape the rest of the dialog reads.
   */
  const data = useMemo((): ConversationDebugPayload | undefined => {
    const pages = debug.data?.pages;
    if (!pages || pages.length === 0) return undefined;

    return {
      ...pages[0],
      chat_sessions: {
        ...pages[pages.length - 1].chat_sessions,
        items: pages.flatMap((page) => page.chat_sessions.items),
      },
    };
  }, [debug.data]);

  return { debug, data };
}
